import { useMemo } from "react"
import {
    selectIsSysAdmin,
    selectUserName
} from "../Helpers/Functions/Selectors/auth"
import { APIOrgMemberRole } from "../Models/API/APIOrgMember"
import { FetchState, hasValue } from "../Models/Client/FetchState"
import { Result } from "../Models/Utility/Result"
import { useOrgs } from "./useOrgs"
import { useAppState } from "./useReduxState"

const ownerResult: Result<APIOrgMemberRole> = Result.ok("OWNER")
const memberResult: Result<APIOrgMemberRole> = Result.ok("MEMBER")

/**
 * Returns role of logged in user for given `orgUsername` wrapped by `FetchState`.
 * If `orgUsername` is a private account, `Result.ok("OWNER")` will be returned.
 */
export function useMyOrgRole(
    orgUsername: string
): FetchState<APIOrgMemberRole> {
    const orgsFetch = useOrgs()
    const username = useAppState(selectUserName)
    const isSysAdmin = useAppState(selectIsSysAdmin)

    return useMemo(() => {
        if (!hasValue(orgsFetch)) {
            return orgsFetch
        }

        if (isSysAdmin || username === orgUsername) {
            return ownerResult
        }

        const role = orgsFetch.value.find(
            member => member.orgUsername === orgUsername
        )?.role

        return role === undefined
            ? Result.error(`No role for organization ${orgUsername}`)
            : role === "MEMBER"
            ? memberResult
            : ownerResult
    }, [isSysAdmin, orgUsername, orgsFetch, username])
}
